import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { SortingDirection, Table } from 'app/components/ui/Table'
import { Container } from 'app/components/ui/Container'
import { Text } from 'app/components/ui/Text'

import {
  filterCompanyDictionary,
  filterStatusDictionary,
  getActiveList,
  QuotationStatusFilters,
} from './utils'
import { QuotationIndex } from 'EstraSuppliers'
import { formatDateShort } from 'app/utils/date'
import { routes } from 'app/routes'
import {
  QuotationStatusDictionary,
  statusTextColors,
} from 'app/utils/quotations'
import { useHistory } from 'react-router-dom'
import { themeEstra } from 'app/styles/themeEstra'
import { Input } from 'app/components/ui/form/Input'

const INITIAL_SORT_DIRECTION: SortingDirection = 'DESC'
const INITIAL_SORT_KEY = 'requested_at'

const tableHeadings = [
  {
    key: 'requested_at',
    label: 'Data richiesta',
    sortable: true,
  },
  {
    key: 'company_name',
    label: 'Azienda',
    sortable: true,
  },
  {
    key: 'description',
    label: 'Oggetto del preventivo',
    sortable: false,
  },
  {
    key: 'status',
    label: 'Stato',
    sortable: true,
  },
]

interface Props {
  allQuotations: QuotationIndex
}

export const QuotationsTable: React.FC<Props> = ({ allQuotations }) => {
  const { push } = useHistory()

  const [sortedQuotations, setSortedQuotations] = useState<QuotationIndex>([])
  const [activeSortDirection, setActiveSortDirection] = useState<
    SortingDirection
  >(INITIAL_SORT_DIRECTION)

  const [activeSortKey, setActiveSortKey] = useState(INITIAL_SORT_KEY)

  const sortList = (
    list: QuotationIndex,
    sortKey: string,
    sortDirection: SortingDirection
  ) => {
    const newList = [...list].sort((a, b) => {
      return a[sortKey] > b[sortKey] ? -1 : 1
    })
    setActiveSortDirection(sortDirection)
    setActiveSortKey(sortKey)
    setSortedQuotations(sortDirection === 'DESC' ? newList : newList.reverse())
  }

  const [activeStatusFilter, setActiveStatusFilter] = useState<
    QuotationStatusFilters
  >('all')

  const [activeCompanyFilter, setActiveCompanyFilter] = useState<string>('all')

  // when filter changes
  useEffect(() => {
    const activeList = getActiveList(
      allQuotations,
      activeStatusFilter,
      activeCompanyFilter
    )
    sortList(activeList, INITIAL_SORT_KEY, INITIAL_SORT_DIRECTION)
  }, [activeStatusFilter, activeCompanyFilter])

  return (
    <QuotationsTableContainer>
      <div>
        <TableHeader>
          <RequestCounter
            variant={'s16'}
            color={themeEstra.color.primari.grigioEstra}
            weight='700'
          >
            Totale Richieste: {sortedQuotations?.length}{' '}
          </RequestCounter>
          <FiltersWrapper>
            <Text
              variant={'s16'}
              color={themeEstra.color.primari.grigioEstra}
              weight='700'
            >
              Filtra per:
            </Text>
            {filterCompanyDictionary(allQuotations).length > 2 && ( // Show filter only if at least two company are present, > 2 cause there is the "All" label
              <SelectWrapper>
                <Select
                  type='select'
                  selectCaretSize='small'
                  value={activeCompanyFilter}
                  onChange={value => {
                    setActiveCompanyFilter(value as string)
                  }}
                >
                  {filterCompanyDictionary(allQuotations).map(filter => (
                    <option key={filter.value} value={filter.value}>
                      {filter.label}
                    </option>
                  ))}
                </Select>
              </SelectWrapper>
            )}
            <SelectWrapper>
              <Select
                type='select'
                selectCaretSize='small'
                value={activeStatusFilter}
                onChange={value => {
                  setActiveStatusFilter(value as QuotationStatusFilters)
                }}
              >
                {Object.keys(filterStatusDictionary).map((filter: string) => (
                  <option key={filter} value={filter}>
                    {
                      filterStatusDictionary[filter as QuotationStatusFilters]
                        .label
                    }
                  </option>
                ))}
              </Select>
            </SelectWrapper>
          </FiltersWrapper>
        </TableHeader>
        <Table
          headings={tableHeadings}
          sortKey={activeSortKey}
          sortDirection={activeSortDirection}
          clickableRows
          hover
          onSorting={(key, direction) => {
            sortList(sortedQuotations, key, direction)
          }}
        >
          <tbody>
            {sortedQuotations.map(item => (
              <tr
                key={item.id}
                onClick={() => {
                  push(routes.quotation.makeUrl(item.id))
                }}
              >
                <td>
                  {item.requested_at ? formatDateShort(item.requested_at) : '-'}
                </td>
                <td>{item.company_name}</td>
                <td>
                  <Text weight='700'>{item.name}</Text>
                </td>
                <td>
                  <StatusText
                    color={
                      statusTextColors[item.quotation_status || item.status]
                    }
                  >
                    {
                      QuotationStatusDictionary[
                        item.quotation_status || item.status
                      ]
                    }
                  </StatusText>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </QuotationsTableContainer>
  )
}

const QuotationsTableContainer = styled(Container)`
  @media (max-width: ${({ theme }) => theme.bp.tablet}) {
    padding-top: 0rem;
  }
`
const TableHeader = styled.div`
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${({ theme }) => theme.bp.tablet}) {
    justify-content: center;
    margin-bottom: 2rem;
  }
`

const RequestCounter = styled(Text)`
  @media (max-width: ${({ theme }) => theme.bp.tablet}) {
    display: none;
  }
`

const FiltersWrapper = styled.div`
  display: flex;
  align-items: center;

  > * {
    margin-right: 2rem;
  }

  @media (max-width: ${({ theme }) => theme.bp.tablet}) {
    flex-direction: column;
    width: 100%;
    > * {
      margin-top: 1rem;
      margin-right: 0rem;
    }
  }
`

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: ${({ theme }) => theme.bp.tablet}) {
    width: 100%;

    max-width: 500px;
  }
`

const Select = styled(Input)`
  width: 150px;

  @media (max-width: ${({ theme }) => theme.bp.tablet}) {
    width: 100%;
  }
`

const StatusText = styled.p<{ color?: string }>`
  color: ${prop => prop.color};
`
